import React from "react";
import { useTranslation } from "react-i18next";
import type { TooltipRenderProps } from "react-joyride";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { useTour } from "@/hooks/useTour";

export const TourTooltip: React.FC<TooltipRenderProps> = ({
	index,
	size,
	tooltipProps,
	isLastStep,
}) => {
	const { t } = useTranslation();
	const tour = useTour();
	const isFirst = index === 0;

	const def = tour.steps[index];
	const title = def ? t(def.titleKey) : "";
	const body = def ? t(def.bodyKey) : "";

	return (
		<div {...tooltipProps} className="max-w-sm">
			<Card className="relative w-[320px] max-w-[90vw] p-5 shadow-xl border-border">
				<button
					type="button"
					onClick={tour.skip}
					aria-label={t("tour.buttons.close")}
					className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors"
				>
					<X className="h-4 w-4" />
				</button>
				<div className="pr-6">
					<h3 className="text-base font-semibold text-foreground">{title}</h3>
					<p className="mt-2 text-sm text-muted-foreground leading-relaxed">{body}</p>
				</div>
				<div className="mt-5 flex items-center justify-center gap-1.5">
					{Array.from({ length: size }).map((_, i) => (
						<span
							key={i}
							className={cn(
								"h-1.5 rounded-full transition-all",
								i === index ? "w-4 bg-primary" : "w-1.5 bg-muted-foreground/30",
							)}
						/>
					))}
				</div>
				<div className="mt-5 flex items-center justify-between gap-2">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="text-muted-foreground"
						onClick={tour.skip}
					>
						{t("tour.buttons.skip")}
					</Button>
					<div className="flex items-center gap-2">
						{!isFirst && (
							<Button type="button" variant="outline" size="sm" onClick={tour.prev}>
								{t("tour.buttons.prev")}
							</Button>
						)}
						<Button
							type="button"
							size="sm"
							onClick={isLastStep ? tour.finish : tour.next}
						>
							{isLastStep ? t("tour.buttons.finish") : t("tour.buttons.next")}
						</Button>
					</div>
				</div>
			</Card>
		</div>
	);
};
